import type Assessment from '@app/src/types/assessment'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import { SustainabilityArea } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopic } from '@app/src/types/topicalRisk'
import type { BaselineBenchmark, BenchmarkData } from './benchmark/types'

export const PROFILE_AREA_ORDER: SustainabilityArea[] = [
  SustainabilityArea.Environment,
  SustainabilityArea.Social,
  SustainabilityArea.Governance,
  SustainabilityArea.SupplyChain,
]

const OWN_OPERATIONS_SECTION = 1
const SUPPLY_CHAIN_SECTION = 2

export enum ScoreBand {
  VeryLow = 0, // 0-20
  Low = 1, // 20-40
  Medium = 2, // 40-60
  High = 3, // 60-80
  VeryHigh = 4, // 80-100
}

export const clampPercent = (value: number): number => Math.max(0, Math.min(100, value))

export const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10

export function scoreToBand(score: number): ScoreBand {
  if (score < 20) return ScoreBand.VeryLow
  if (score < 40) return ScoreBand.Low
  if (score < 60) return ScoreBand.Medium
  if (score < 80) return ScoreBand.High
  return ScoreBand.VeryHigh
}

export function computeDelta(score: number, peerMean: number): number {
  return roundToOneDecimal(score - peerMean)
}

export interface TemplateScores {
  overall?: number
  ownOps?: number
  supplyChain?: number
  isScreened: boolean
  periodName?: string
}

export function sectionScoreToPercent(rawScore?: number, maximumScore?: number): number | undefined {
  if (rawScore == null || maximumScore == null || maximumScore <= 0) return undefined
  return Math.round(clampPercent((rawScore / maximumScore) * 100))
}

function sectionScore(assessment: Assessment, sectionNumber: number): number | undefined {
  const key = `section${sectionNumber}Score` as keyof Assessment
  return assessment[key] as number | undefined
}

function sectionMaximum(assessment: Assessment, sectionNumber: number): number | undefined {
  return assessment.assessmentTemplate?.sections?.find(section => section.assessmentSectionNumber === sectionNumber)
    ?.maximumScore
}

export function computeTemplateScores(assessment: Assessment): TemplateScores {
  return {
    overall: assessment.percentage ?? undefined,
    ownOps: sectionScoreToPercent(
      sectionScore(assessment, OWN_OPERATIONS_SECTION),
      sectionMaximum(assessment, OWN_OPERATIONS_SECTION),
    ),
    supplyChain: sectionScoreToPercent(
      sectionScore(assessment, SUPPLY_CHAIN_SECTION),
      sectionMaximum(assessment, SUPPLY_CHAIN_SECTION),
    ),
    isScreened: assessment.isScreened,
    periodName: assessment.periodName,
  }
}

export interface BaselineScore {
  level: number
  levelsCount: number
  peerLevel?: number
  delta?: number
}

function makeBaselineScore(level: number, levelsCount: number, peerLevel: number | undefined): BaselineScore {
  return { level, levelsCount, peerLevel, delta: peerLevel !== undefined ? computeDelta(level, peerLevel) : undefined }
}

export function baselineLevel(baseline: Assessment): { level: number; levelsCount: number } | undefined {
  const level = baseline.totalLevel
  const levelsCount = baseline.assessmentTemplate?.levels
  if (level == null || !levelsCount) return undefined
  return { level, levelsCount }
}

export function baselineAreaPeerLevel(
  baseline: BaselineBenchmark | undefined,
  area: SustainabilityArea,
): number | undefined {
  return baseline?.areaMeanLevels[area]
}

export function pickLatestPerTemplate(assessments: Assessment[]): Assessment[] {
  const seen = new Set<number>()
  const latest: Assessment[] = []
  for (const assessment of assessments) {
    if (seen.has(assessment.assessmentTemplateId)) continue
    seen.add(assessment.assessmentTemplateId)
    latest.push(assessment)
  }
  return latest
}

export function hasSectionBreakdown(scores?: TemplateScores): boolean {
  return scores?.ownOps !== undefined || scores?.supplyChain !== undefined
}

export function computeBaselineByArea(
  topics: SustainabilityTopic[],
  baseline: Assessment | undefined,
): Map<SustainabilityArea, { level: number; levelsCount: number }> {
  const byArea = new Map<SustainabilityArea, { level: number; levelsCount: number }>()
  const levelsCount = baseline?.assessmentTemplate?.levels
  if (!baseline || !levelsCount) return byArea

  for (const topic of topics) {
    if (byArea.has(topic.sustainabilityArea)) continue
    const section = topic.assessmentTemplateSection
    if (!section) continue
    const key = `section${section.assessmentSectionNumber}Level` as keyof Assessment
    const level = baseline[key] as number | undefined
    if (level !== undefined) byArea.set(topic.sustainabilityArea, { level, levelsCount })
  }

  return byArea
}

export interface SubScore {
  score?: number
  peerMean?: number
  delta?: number
}

export interface ProfileTopicRow {
  topicId: number
  name: string
  assessmentTemplateId?: number
  periodName?: string
  hasSectionBreakdown: boolean
  isScreened: boolean
  overall: SubScore
  ownOps: SubScore
  supplyChain: SubScore
  riskStatus?: RiskStatus
}

export interface ProfileAreaSection {
  area: SustainabilityArea
  rows: ProfileTopicRow[]
}

export interface BuildAreaSectionsOptions {
  riskStatusByTemplate?: Map<number, RiskStatus>
}

function makeSubScore(score: number | undefined, peerMean: number | undefined): SubScore {
  const delta = score !== undefined && peerMean !== undefined ? computeDelta(score, peerMean) : undefined
  return { score, peerMean, delta }
}

export function buildAreaSections(
  topics: SustainabilityTopic[],
  scores: Map<number, TemplateScores>,
  benchmark: BenchmarkData | undefined,
  options: BuildAreaSectionsOptions = {},
): ProfileAreaSection[] {
  const { riskStatusByTemplate } = options

  return PROFILE_AREA_ORDER.map(area => {
    const rows: ProfileTopicRow[] = topics
      .filter(topic => topic.sustainabilityArea === area)
      .map(topic => {
        const templateScores =
          topic.assessmentTemplateId !== undefined ? scores.get(topic.assessmentTemplateId) : undefined
        const topicBenchmark = benchmark?.topics[topic.id]
        return {
          topicId: topic.id,
          name: topic.name,
          assessmentTemplateId: topic.assessmentTemplateId,
          periodName: templateScores?.periodName,
          hasSectionBreakdown: hasSectionBreakdown(templateScores),
          isScreened: templateScores?.isScreened ?? false,
          overall: makeSubScore(templateScores?.overall, topicBenchmark?.mean),
          ownOps: makeSubScore(templateScores?.ownOps, topicBenchmark?.ownOpsMean),
          supplyChain: makeSubScore(templateScores?.supplyChain, topicBenchmark?.supplyChainMean),
          riskStatus:
            topic.assessmentTemplateId !== undefined
              ? riskStatusByTemplate?.get(topic.assessmentTemplateId)
              : undefined,
        }
      })

    return { area, rows }
  }).filter(section => section.rows.length > 0)
}

export function hasTopicScores(sections: ProfileAreaSection[]): boolean {
  return sections.some(section => section.rows.some(row => row.overall.score !== undefined))
}

export interface BaselineAreaRow extends BaselineScore {
  area: SustainabilityArea
}

export function buildBaselineAreaRows(
  topics: SustainabilityTopic[],
  baseline: Assessment,
  peerBaseline: BaselineBenchmark | undefined,
): BaselineAreaRow[] {
  const baselineByArea = computeBaselineByArea(topics, baseline)
  return PROFILE_AREA_ORDER.flatMap(area => {
    const entry = baselineByArea.get(area)
    if (!entry) return []
    return [{ area, ...makeBaselineScore(entry.level, entry.levelsCount, baselineAreaPeerLevel(peerBaseline, area)) }]
  })
}

export function computeOverallScore(sections: ProfileAreaSection[]): number | undefined {
  const scores = sections
    .flatMap(section => section.rows.map(row => row.overall.score))
    .filter((s): s is number => s !== undefined)
  if (scores.length === 0) return undefined
  return roundToOneDecimal(scores.reduce((sum, s) => sum + s, 0) / scores.length)
}
